import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { CompanyTrack, clientApi } from '@/api/client'
import ErrorBox from '@/components/ErrorBox'
import Topbar from '@/components/Topbar'
import UniverseTabs from '@/components/UniverseTabs'
import { Posture, SkeletonPanel } from '@/components/primitives'
import { timeAgo } from '@/utils/format'

function summarizeText(value: any, fallback?: string | null): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim()
  if (Array.isArray(value)) {
    const joined = value
      .map((item) => {
        if (typeof item === 'string') return item.trim()
        if (!item || typeof item !== 'object') return ''
        return String(item.title || item.name || item.label || item.summary || item.text || '').trim()
      })
      .filter(Boolean)
      .slice(0, 2)
      .join(' ')
    if (joined) return joined
  }
  if (fallback && fallback.trim()) return fallback.trim()
  return null
}

function firstNumber(...values: any[]): number | null {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value
  }
  return null
}

function distinctText(value?: string | null, other?: string | null): string | null {
  const text = typeof value === 'string' ? value.trim() : ''
  if (!text) return null
  const otherText = typeof other === 'string' ? other.trim() : ''
  if (otherText && text.toLowerCase() === otherText.toLowerCase()) return null
  return text
}

export default function Companies() {
  const [tracks, setTracks] = useState<CompanyTrack[] | null>(null)
  const [groups, setGroups] = useState<any[] | null>(null)
  const [error, setError] = useState<any>(null)
  const [ticker, setTicker] = useState('')
  const [search, setSearch] = useState('')
  const [tracking, setTracking] = useState(false)

  useEffect(() => {
    let cancelled = false
    Promise.allSettled([clientApi.companies(), clientApi.companyGroups()])
      .then((res) => {
        if (cancelled) return

        const companiesRes = res[0]
        const groupsRes = res[1]

        if (companiesRes.status === 'fulfilled') {
          setTracks(companiesRes.value.companies || [])
        } else {
          setTracks([])
        }

        if (groupsRes.status === 'fulfilled') {
          setGroups(groupsRes.value.groups || [])
        } else {
          setGroups([])
        }

        const errs: string[] = []
        if (companiesRes.status === 'rejected') errs.push(companiesRes.reason?.message || 'Failed loading companies')
        if (groupsRes.status === 'rejected') errs.push(groupsRes.reason?.message || 'Failed loading groups')
        if (errs.length > 0) setError(new Error(errs.join(' | ')))
      })
      .catch((e) => {
        if (!cancelled) setError(e)
      })
    return () => { cancelled = true }
  }, [])

  async function handleTrack() {
    const t = ticker.trim().toUpperCase()
    if (!t) return
    setTracking(true)
    try {
      await clientApi.trackCompany(t)
      const c = await clientApi.companies()
      setTracks(c.companies || [])
      setTicker('')
    } catch (ex) {
      setError(ex)
    } finally {
      setTracking(false)
    }
  }

  const searchQuery = search.trim().toLowerCase()
  const filteredTracks = useMemo(() => {
    const rows = tracks || []
    if (!searchQuery) return rows
    return rows.filter((t) => {
      const c: any = t.company || {}
      const card = t.card_summary || {}
      const artifact = t.artifact || {}
      const memoCall = t.memo_call || card.memo_call || null
      const forecastPosture = t.forecast_posture || card.forecast_posture || null
      const text = [
        c.ticker,
        c.company_name,
        c.sector,
        c.industry,
        t.current_view_summary,
        t.what_changed_summary,
        t.latest_report_summary,
        card.current_view_summary,
        card.what_changed_summary,
        artifact.current_view,
        artifact.what_changed,
        memoCall?.headline,
        memoCall?.summary,
        forecastPosture?.headline,
        forecastPosture?.label,
        forecastPosture?.assessment,
      ].filter(Boolean).join(' ').toLowerCase()
      return text.includes(searchQuery)
    })
  }, [tracks, searchQuery])

  const filteredGroups = useMemo(() => {
    const rows = groups || []
    if (!searchQuery) return rows
    return rows.filter((g: any) => [g.name, g.description].filter(Boolean).join(' ').toLowerCase().includes(searchQuery))
  }, [groups, searchQuery])

  if (!tracks && !error) return (
    <div className="container">
      <Topbar subtitle="Companies" />
      <UniverseTabs />
      <SkeletonPanel kind="report" count={4} marginTop={0} />
    </div>
  )

  return (
    <div className="container">
      <Topbar subtitle="Companies" />
      <UniverseTabs />

      <div className="row" style={{ gap: 8, alignItems: 'stretch', flexWrap: 'wrap' }}>
        <input
          className="input"
          placeholder="Enter ticker to track (e.g. AAPL)"
          value={ticker}
          onChange={(e) => setTicker(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleTrack()}
          style={{ maxWidth: 300 }}
        />
        <button className="btn primary" onClick={handleTrack} disabled={tracking || !ticker.trim()}>
          {tracking ? 'Tracking...' : 'Track'}
        </button>
        <div style={{ flex: '1 1 280px' }}>
          <input
            className="input"
            placeholder="Search tracked companies by name, ticker, sector, or memo text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search companies"
          />
        </div>
        {search.trim() && (
          <button className="btn" onClick={() => setSearch('')}>
            Clear
          </button>
        )}
      </div>

      {tracks && tracks.length > 0 && (
        <div className="muted" style={{ marginTop: 10, fontSize: 12 }}>
          {search.trim()
            ? `${filteredTracks.length} of ${tracks.length} companies match "${search.trim()}"`
            : `${tracks.length} tracked compan${tracks.length === 1 ? 'y' : 'ies'}`}
        </div>
      )}

      {error && <ErrorBox error={error} />}

      <div className="grid" style={{ marginTop: 16 }}>
        {filteredTracks.map((t) => {
          const c: any = t.company || {}
          const card = t.card_summary || {}
          const artifact = t.artifact || {}
          const memoCall = t.memo_call || card.memo_call || null
          const forecastPosture = t.forecast_posture || card.forecast_posture || null
          const stanceDescription = distinctText(forecastPosture?.assessment || memoCall?.summary, forecastPosture?.headline || memoCall?.headline)
          const postureHeadline = forecastPosture?.headline || memoCall?.headline || memoCall?.summary
          const postureLabel = forecastPosture?.label || null
          const currentView = summarizeText(
            t.current_view_summary || card.current_view_summary || artifact.current_view,
            t.latest_report_summary || null,
          )
          const whatChanged = summarizeText(
            t.what_changed_summary || card.what_changed_summary || artifact.what_changed,
            null,
          )
          const approvedCount = firstNumber(
            t.approved_question_count,
            card.approved_question_count,
            artifact.approved_question_count,
          )
          const watchpointCount = firstNumber(
            t.watchpoint_count,
            card.watchpoint_count,
            Array.isArray(artifact.watchpoints) ? artifact.watchpoints.length : null,
          )
          const forecastedCount = firstNumber(
            t.forecasted_question_count,
            card.forecasted_question_count,
          )
          const lastMeaningfulUpdateAt =
            t.last_meaningful_update_at ||
            card.last_meaningful_update_at ||
            artifact.latest_update_at ||
            c.last_research_at ||
            null
          return (
            <Link key={t.track_id} to={`/universe/companies/${c.id}`} className="company-card">
              <div className="row" style={{ justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 700 }}>{c.ticker}</div>
                  <div style={{ fontSize: 14 }}>{c.company_name}</div>
                </div>
                <div style={{ textAlign: 'right', display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {c.sector && <span className="pill">{c.sector}</span>}
                </div>
              </div>
              {(postureHeadline || stanceDescription || postureLabel || forecastPosture?.most_likely_case || forecastPosture?.next_proof_point) && (
                <div className="company-card-posture">
                  <Posture
                    compact
                    headline={postureHeadline}
                    label={postureLabel}
                    summary={stanceDescription}
                    mostLikelyCase={forecastPosture?.most_likely_case || null}
                    nextProofPoint={forecastPosture?.next_proof_point || null}
                  />
                </div>
              )}
              {currentView ? (
                <div style={{ marginTop: 10 }}>
                  <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Current View
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.5, marginTop: 2 }}>{currentView.slice(0, 220)}</div>
                </div>
              ) : (
                <div className="muted" style={{ marginTop: 10, fontSize: 12, fontStyle: 'italic' }}>
                  Research scheduled &mdash; an initial report will be generated shortly.
                </div>
              )}
              {whatChanged && (
                <div style={{ marginTop: 10 }}>
                  <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    What Changed
                  </div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 2, lineHeight: 1.5 }}>
                    {whatChanged.slice(0, 220)}
                  </div>
                </div>
              )}
              <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
                {typeof approvedCount === 'number' && (
                  <span className="pill ok">{approvedCount} question{approvedCount === 1 ? '' : 's'}</span>
                )}
                {typeof forecastedCount === 'number' && (
                  <span className="pill">{forecastedCount} current forecast{forecastedCount === 1 ? '' : 's'}</span>
                )}
                {typeof watchpointCount === 'number' && (
                  <span className="pill">{watchpointCount} watchpoint{watchpointCount === 1 ? '' : 's'}</span>
                )}
                {lastMeaningfulUpdateAt && (
                  <span className="pill">Updated {timeAgo(lastMeaningfulUpdateAt)}</span>
                )}
              </div>
            </Link>
          )
        })}

        {tracks && tracks.length === 0 && (
          <div className="card">
            <p className="muted">No companies tracked yet. Enter a ticker above or select a group below.</p>
          </div>
        )}

        {tracks && tracks.length > 0 && filteredTracks.length === 0 && (
          <div className="card">
            <p className="muted">No tracked companies match your search.</p>
          </div>
        )}
      </div>

      {groups && filteredGroups.length > 0 && (
        <>
          <h2 style={{ marginTop: 28, fontSize: 16, fontWeight: 700 }}>Company Groups</h2>
          <div className="grid">
            {filteredGroups.map((g: any) => (
              <Link key={g.group_id} to={`/universe/companies/groups/${g.group_id}`} className="company-card">
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{g.name}</div>
                    {g.description && (
                      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                        {g.description}
                      </div>
                    )}
                  </div>
                  {g.is_public && <span className="pill ok">Public</span>}
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
